import React, { FC, useState } from 'react';
import { Typography, LinkWrapper, Stars } from '@milanlaser/mlcomponents';

import type { MapLocation } from '../Map/Map';

export interface ClinicLocationMapCardProps {
    readonly className?: string;
    readonly isSelected?: boolean;
    readonly location: MapLocation;
    readonly showDistance?: boolean;
    readonly onCardClick?: () => void;
}

export const ClinicLocationMapCard: FC<ClinicLocationMapCardProps> = ({
    location,
    showDistance = true,
    isSelected = false,
    className,
    onCardClick,
}) => {
    const { name, address, rating } = location;
    const [isHovered, setIsHovered] = useState(false);

    // Using inline styles to override CSS specificity issues:
    // - Global CSS reset (* { padding: 0 }) overrides Tailwind p-5 class
    // - Other CSS rules cross out border-milan-blue class
    // - Custom hover effect since Tailwind hover classes weren't working properly
    const cardEventClasses = `rounded-[30px] shadow-sm transition-all cursor-pointer ${className || ''}`;
    const cardEventStyles = {
        backgroundColor: isHovered ? 'rgba(255, 255, 255, 0.5)' : 'white',
        border: isSelected ? '1px solid var(--milan-turquoise)' : '1px solid transparent',
        padding: '1.25rem',
    };

    const handleCardClick = (e: React.MouseEvent) => {
        // Don't trigger card click if clicking on the "View Clinic Info" link
        const target = e.target as HTMLElement;
        if (target.closest('a')) {
            return;
        }
        onCardClick?.();
    };

    return (
        <div
            className={cardEventClasses}
            style={cardEventStyles}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleCardClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onCardClick?.();
                }
            }}
            aria-label={`Select ${name} location`}
        >
            <div className="flex flex-col gap-2 h-full">
                <div className="flex flex-col gap-2 items-start justify-between md:flex-row md:gap-4">
                    <Typography tag="h3" variant="h3-large" color="Turquoise" className="flex-1 min-w-0 truncate !m-0">
                        {name}
                    </Typography>
                </div>

                {rating && rating?.value > 0 && (
                    <div
                        className="flex items-center gap-2 !m-0"
                        role="img"
                        aria-label={`${rating.value} out of 5 stars`}
                        data-testid="star-rating"
                    >
                        <div className="flex items-center gap-2 !m-0">
                            {rating.value > 0 && <Stars rating={rating.value} />}
                            {rating.reviewCount > 0 && (
                                <div className="flex !gap-2 text-milan-md-grey items-center">
                                    {rating?.value > 0 && (
                                        <Typography tag="p" className="text-milan-blue !text-sm !m-0">
                                            {rating.value}
                                        </Typography>
                                    )}
                                    <Typography tag="p" className="text-milan-blue !text-sm !m-0">
                                        {rating.reviewCount} Review{rating.reviewCount > 1 ? 's' : ''}
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-5 justify-between h-full">
                    <div className="space-y-1">
                        <Typography tag="p" className="!m-0">
                            {address?.line1}
                            {address?.line2 && (
                                <>
                                    <br />
                                    {address.line2}
                                </>
                            )}
                        </Typography>
                    </div>

                    <div className="flex justify-between items-center">
                        <LinkWrapper
                            href={location.href}
                            target="_self"
                            className="inline-flex items-center text-milan-blue transition-colors w-fit hover:text-milan-secondary-blue"
                        >
                            <Typography tag="span" variant="p" color="Blue">
                                View Clinic Info
                            </Typography>
                        </LinkWrapper>

                        {showDistance && location.distance && (
                            <Typography tag="span" variant="p" className="flex-shrink-0 text-left md:text-right">
                                {location.distance.toFixed(1)} mi
                            </Typography>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
